'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { getAuthSession } from '../lib/authSession';
import { getDesktopPushEndpoint, hasDesktopBridge } from '../lib/desktopBridge';
import { useProjectsSubscriptionStatus } from '../hooks/queries/usePushNotifications';
import { cn } from '../lib/cn';
import { BaseModal } from './BaseModal';
import ProjectSettingsView from './ProjectSettingsView';
import ProjectTagsView from './ProjectTagsView';
import ProjectMembersView from './ProjectMembersView';
import ProjectRepositoriesView from './ProjectRepositoriesView';
import NotificationSettingsView from './NotificationSettingsView';
import RunnerDispatchPolicySettingsView from './RunnerDispatchPolicySettingsView';
import RunnerScheduleSettingsView from './RunnerScheduleSettingsView';
import ServiceTokenSettingsView from './ServiceTokenSettingsView';
import { useProjectPermissions } from '../hooks/useProjectPermissions';

type ProjectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  // 프로젝트가 삭제되면 호출된다. 부모가 모달 닫기/홈 이동 등 후처리를 수행한다.
  onProjectDeleted?: (projectId: string) => void;
  initialTab?: ProjectTab;
};

export type ProjectTab =
  | 'settings'
  | 'members'
  | 'repositories'
  | 'dispatchPolicies'
  | 'schedules'
  | 'serviceTokens'
  | 'notifications';
const projectTabs = [
  'settings',
  'members',
  'repositories',
  'dispatchPolicies',
  'schedules',
  'serviceTokens',
  'notifications',
] as const satisfies readonly ProjectTab[];

export function ProjectModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  onProjectDeleted,
  initialTab = 'settings',
}: ProjectModalProps) {
  const t = useTranslations('workspace.page');
  const tCommon = useTranslations('common');
  const session = useMemo(() => getAuthSession(), []);
  const [activeTab, setActiveTab] = useState<ProjectTab>('settings');
  const [pushEndpoint, setPushEndpoint] = useState<string | null>(null);

  // 프로젝트가 바뀌거나 새로 열리면 호출자가 요청한 탭으로 맞춘다.
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [initialTab, isOpen, projectId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (hasDesktopBridge()) {
      setPushEndpoint(getDesktopPushEndpoint());
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushEndpoint(null);
      return;
    }

    const syncEndpoint = async () => {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushEndpoint(subscription?.endpoint ?? null);
    };

    void syncEndpoint().catch(() => {
      setPushEndpoint(null);
    });
  }, []);

  const { data: subscriptionStatuses = [] } = useProjectsSubscriptionStatus(session?.token ?? null, pushEndpoint);
  const isSubscribed = subscriptionStatuses.some((row) => row.projectId === projectId && row.isSubscribed);

  // 서비스 토큰 목록은 뷰어에게 403이므로(`serviceTokens.read` 제외 역할) 탭 자체를 감춘다.
  const { can } = useProjectPermissions({ projectId });
  const visibleTabs = useMemo(
    () =>
      projectTabs.filter(
        (tab) => (tab !== 'serviceTokens' || can('serviceTokens.read')) && (tab !== 'schedules' || can('runners.read')),
      ),
    [can],
  );
  const resolvedTab = visibleTabs.includes(activeTab) ? activeTab : 'settings';

  // 탭 줄이 가로 스크롤되므로, 선택된 탭이 잘려 있으면 보이는 위치까지 밀어 준다.
  // scrollIntoView는 조상(모달 본문)까지 같이 스크롤시키므로 탭 줄의 scrollLeft만 직접 건드린다.
  const tabListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const list = tabListRef.current;
    const active = list?.querySelector<HTMLElement>(`#project-tab-${resolvedTab}`);
    if (!list || !active) return;

    const listRect = list.getBoundingClientRect();
    const tabRect = active.getBoundingClientRect();
    // 선택 탭을 가장자리에 딱 맞추면 뒤에 항목이 더 있다는 게 안 보인다.
    // 이동 방향 쪽 이웃 탭이 이만큼 드러나도록 더 밀어 준다(끝단은 브라우저가 알아서 클램프).
    const peek = 32;
    if (tabRect.left < listRect.left) {
      list.scrollBy({ left: tabRect.left - listRect.left - peek, behavior: 'smooth' });
    } else if (tabRect.right > listRect.right) {
      list.scrollBy({ left: tabRect.right - listRect.right + peek, behavior: 'smooth' });
    }
  }, [isOpen, resolvedTab]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="bottomSheet"
      size="3xl"
      height="h-[85vh] max-h-[620px] sm:h-[70vh]"
      className="!p-0 flex flex-col overflow-hidden bg-base-100"
    >
      <div className="flex items-center justify-between gap-3 border-b border-base-200 px-5 py-4">
        <h2 className="min-w-0 truncate text-base font-semibold text-base-content">{projectName}</h2>
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-circle shrink-0"
          aria-label={tCommon('actions.close')}
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="border-b border-base-200 px-5 py-3">
        {/* 탭이 늘어나도 좁은 화면에서 글자가 눌리므로, 폭이 모자라면 가로 스크롤로 넘긴다.
            각 탭은 grow+basis-0으로 공간이 남을 때만 균등 분배되고, 모자라면 내용 폭을 지켜 넘친다. */}
        <div
          ref={tabListRef}
          role="tablist"
          aria-label={projectName}
          className="flex w-full overflow-x-auto rounded-xl bg-base-200 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              id={`project-tab-${tab}`}
              type="button"
              role="tab"
              aria-selected={resolvedTab === tab}
              aria-controls={`project-tabpanel-${tab}`}
              className={cn(
                'btn btn-ghost btn-sm min-h-9 shrink-0 grow basis-0 whitespace-nowrap rounded-lg border-0 px-3 text-sm font-medium',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                resolvedTab === tab && 'bg-base-100 text-primary shadow-xs',
              )}
              onClick={() => setActiveTab(tab)}
            >
              {t(`projectTabs.${tab}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {resolvedTab === 'settings' ? (
          <div
            id="project-tabpanel-settings"
            role="tabpanel"
            aria-labelledby="project-tab-settings"
            className="space-y-4"
          >
            <ProjectSettingsView
              projectId={projectId}
              afterGeneral={<ProjectTagsView projectId={projectId} />}
              onProjectDeleted={onProjectDeleted}
            />
          </div>
        ) : resolvedTab === 'members' ? (
          <div id="project-tabpanel-members" role="tabpanel" aria-labelledby="project-tab-members">
            <ProjectMembersView projectId={projectId} />
          </div>
        ) : resolvedTab === 'repositories' ? (
          <div id="project-tabpanel-repositories" role="tabpanel" aria-labelledby="project-tab-repositories">
            <ProjectRepositoriesView projectId={projectId} />
          </div>
        ) : resolvedTab === 'dispatchPolicies' ? (
          <div id="project-tabpanel-dispatchPolicies" role="tabpanel" aria-labelledby="project-tab-dispatchPolicies">
            <RunnerDispatchPolicySettingsView projectId={projectId} />
          </div>
        ) : resolvedTab === 'schedules' ? (
          <div id="project-tabpanel-schedules" role="tabpanel" aria-labelledby="project-tab-schedules">
            <RunnerScheduleSettingsView key={projectId} projectId={projectId} />
          </div>
        ) : resolvedTab === 'serviceTokens' ? (
          <div id="project-tabpanel-serviceTokens" role="tabpanel" aria-labelledby="project-tab-serviceTokens">
            <ServiceTokenSettingsView projectId={projectId} />
          </div>
        ) : (
          <div id="project-tabpanel-notifications" role="tabpanel" aria-labelledby="project-tab-notifications">
            <NotificationSettingsView
              projectId={projectId}
              isSubscribed={isSubscribed}
              pushEndpoint={pushEndpoint}
              onPushEndpointChangeAction={(endpoint) => setPushEndpoint(endpoint)}
            />
          </div>
        )}
      </div>
    </BaseModal>
  );
}
